import React, { useMemo, useState } from 'react';
import { Building2, TrendingUp, Users, FileText, Activity, AlertCircle, CheckCircle2, Percent } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { formatCurrencyAmount, formatNumberValue, removeUnderscoresAndCapitalize } from '../../../utils/miscFunctions';
import clsx from 'clsx';
import GTable from '../../elements/GTable';
import { BsCash } from 'react-icons/bs';
import Loader from '../../Loader';


interface CompanyStatisticsPanelProps {
  data?: ICompanyStatistics | null;
  country: string;
  isLoadingCompanyStatistics: boolean;
}

const CompanyStatisticsPanel: React.FC<CompanyStatisticsPanelProps> = ({ data, country, isLoadingCompanyStatistics }) => {
  const [activeIndex1, setActiveIndex1] = useState<number | null>(null);
  const [activeIndex2, setActiveIndex2] = useState<number | null>(null);

  const onPieEnter1 = (_: any, index: number) => {
    setActiveIndex1(index);
  };

  const onPieLeave1 = () => {
    setActiveIndex1(null);
  };

  const onPieEnter2 = (_: any, index: number) => {
    setActiveIndex2(index);
  };

  const onPieLeave2 = () => {
    setActiveIndex2(null);
  };


  const getChartColor = (type: string) => {
    const colors: { [key: string]: string } = {
      BROKER: '#3B82F6',
      INSURER: '#10B981',
      AGENCY: '#8B5CF6',
      FINANCIER: '#F59E0B',
      INTERNAL: '#6B7280',
      REPAIR_CENTER: '#EC4899',
      RIDE_SHARING: '#6366F1',
      FUEL_PROVIDER: '#EAB308',
      OTHER: '#9CA3AF',
      PARTNER: '#3B82F6',
      SERVICE_PROVIDER: '#10B981',
      CLIENT: '#8B5CF6'
    };
    return colors[type] || colors.OTHER;
  };

  const topPerformingCompaniesColumns = useMemo(
    () => [
      {
        Header: 'Company Performance',
        id: 'company-performance',
        columns: [
          {
            Header: 'Rank',
            id: 'rank',
            accessor: (row: any, index: number) => index + 1,
            Cell: ({ cell }: any) => {
              const rank = cell.value;
              return (
                <div className={`text-center w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${rank === 1 ? 'bg-yellow-100 text-yellow-700' :
                  rank === 2 ? 'bg-gray-100 text-gray-700' :
                    rank === 3 ? 'bg-orange-100 text-orange-700' :
                      'bg-blue-50 text-blue-600'
                  }`}>
                  {rank}
                </div>
              );
            }
          },
          {
            Header: 'Company',
            accessor: 'name',
            Cell: (row: any) => {
              return (
                <span className={clsx('')}>
                  {row.cell.value}
                </span>
              );
            }
          },
          {
            Header: 'Users',
            accessor: 'userCount',
            Cell: (row: any) => {
              return (
                <span className={clsx('')}>
                  {formatNumberValue(row.cell.value)}
                </span>
              );
            }
          },

          {
            Header: 'Policies',
            accessor: 'policyCount',
            Cell: (row: any) => {
              return (
                <span className={clsx('')}>
                  {formatNumberValue(row.cell.value)}
                </span>
              );
            }
          },
          {
            Header: 'Total Sales',
            accessor: 'totalSales',
            Cell: (row: any) => {
              return (
                <span className={clsx('')}>
                  {formatCurrencyAmount(row.cell.value)}
                </span>
              );
            }
          },
        ]
      }
    ],
    []
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Company Statistics</h2>
          <p className="text-sm text-gray-500 mt-1 dark:text-gray-200">Overview of all registered companies and their performance</p>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Companies */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-darkBg-mainText">Total Companies</p>
              <p className="text-xl font-bold text-gray-900 dark:text-darkBg-mainText  mt-2">{data?.totalCompanies || "-"}</p>
              <p className="text-xs text-gray-500 mt-1">{data?.recentRegistrations || "-"} new this month</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-2xl">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Active Companies */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-darkBg-mainText">Active Companies</p>
              <p className="text-xl font-bold text-green-600 mt-2">{data?.activeCompanies || "-"}</p>
              <p className="text-xs text-green-600 mt-1 flex items-center">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                {data?.activationRate || "-"}% activation rate
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-2xl">
              <Activity className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Average Sales */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-darkBg-mainText">Avg Sales/Company</p>
              <p className="text-xl font-bold text-purple-600 mt-2">{formatCurrencyAmount(data?.avgSalesPerCompany || 0)}</p>
              <p className="text-xs text-gray-500 mt-1">{data?.companiesWithoutSales || "-"} with no sales</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-2xl">
              <BsCash className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Average Policies */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-darkBg-mainText">Avg Policies/Company</p>
              <p className="text-xl font-bold text-orange-600 mt-2">{formatNumberValue(data?.avgPoliciesPerCompany) || "-"}</p>
              <p className="text-xs text-gray-500 mt-1">Per active company</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-2xl">
              <FileText className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Inactive Companies */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-2xl">
              <AlertCircle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-darkBg-mainText">Inactive Companies</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-darkBg-mainText mt-1">{data?.inactiveCompanies || "-"}</p>
            </div>
          </div>
        </div>

        {/* Companies Without Users */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-100 rounded-2xl">
              <Users className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-darkBg-mainText">Without Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-darkBg-mainText mt-1">{data?.companiesWithoutUsers || "-"}</p>
            </div>
          </div>
        </div>

        {/* Activation Rate */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-2xl">
              <Percent className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-darkBg-mainText">Activation Rate</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{data?.activationRate || "-"}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Companies by Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Companies by Type - Donut Chart */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-4">Active Companies by Type</h3>
          <div className="h-80">
            {isLoadingCompanyStatistics ? (
              <Loader message='Loading chart data...' brollyLoaderFixed />
            ) : Object.keys(data?.activeCompaniesByType || {}).length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center space-y-2">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                    <Activity className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500">No active companies by type data available</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={Object.entries(data?.activeCompaniesByType || {})
                      ?.filter(([, count]) => Number(count) > 0)
                      ?.map(([type, count]) => ({
                        name: removeUnderscoresAndCapitalize(type),
                        value: count
                      }))}
                    cx="50%"
                    cy="50%"
                    className='text-sm'
                    innerRadius={60}
                    outerRadius={activeIndex1 !== null ? 85 : 80}
                    fill="#8884d8"
                    paddingAngle={2}
                    dataKey="value"
                    onMouseEnter={onPieEnter1}
                    onMouseLeave={onPieLeave1}
                  // label={({ name, value }) => `${name}: ${value}`}
                  >
                    {Object.entries(data?.activeCompaniesByType || {})
                      ?.filter(([, count]) => Number(count) > 0)
                      ?.map(([type], index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={activeIndex1 === index ? getChartColor(type) : `${getChartColor(type)}`}
                          style={{
                            filter: activeIndex1 === index ? 'brightness(1.1)' : 'brightness(1.0)',
                            cursor: 'pointer'
                          }}
                        />
                      ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: any) => [`${value} companies`, 'Count']}
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value) => <span className="text-sm">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Companies by Category - Donut Chart */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-4">Active Companies by Category</h3>
          <div className="h-80">
            {isLoadingCompanyStatistics ? (
              <Loader message='Loading chart data...' brollyLoaderFixed />
            ) : Object.keys(data?.activeCompaniesByCategory || {}).length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center space-y-2">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                    <Activity className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500">No active companies by category data available</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={Object.entries(data?.activeCompaniesByCategory || {})
                      .filter(([, count]) => Number(count) > 0)
                      .map(([category, count]) => ({
                        name: removeUnderscoresAndCapitalize(category),
                        value: count,
                        total: (data?.companiesByCategory as any)?.[category] || 0
                      }))}
                    cx="50%"
                    cy="50%"
                    className='text-sm'
                    innerRadius={60}
                    outerRadius={activeIndex2 !== null ? 85 : 80}
                    fill="#8884d8"
                    paddingAngle={2}
                    dataKey="value"
                    onMouseEnter={onPieEnter2}
                    onMouseLeave={onPieLeave2}
                  // label={({ name, value, total }) => `${name}: ${value}/${total}`}
                  >
                    {Object.entries(data?.activeCompaniesByCategory || {})
                      .filter(([, count]) => Number(count) > 0)
                      .map(([category], index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={activeIndex2 === index ? getChartColor(category) : `${getChartColor(category)}`}
                          style={{
                            filter: activeIndex2 === index ? 'brightness(1.1)' : 'brightness(1.0)',
                            cursor: 'pointer'
                          }}
                        />
                      ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: any, name: any, props: any) => [
                      `${value} active of ${props?.payload?.total} total`, // eslint-disable-line react/prop-types
                      'Companies'
                    ]}
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value) => <span className="text-sm">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Top Performing Companies - Split by Broker and Insurer */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performing Brokers */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <div className=" mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">Top Performing Brokers</h3>
            <p className="text-sm text-gray-500 mt-1">Ranked by total sales</p>
          </div>

          <div className="overflow-auto max-h-[400px]">
            <GTable
              columns={topPerformingCompaniesColumns}
              data={data?.topPerformingCompanies
                .filter(company => company.type === 'BROKER')
                .slice(0, 5) || []}
              showSearch={false}
              tableType="approval"
              columnClasses=""
              loading={isLoadingCompanyStatistics}
              classes="h-fit"
            />
          </div>
        </div>

        {/* Top Performing Insurers */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <div className=" mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">Top Performing Insurers</h3>
            <p className="text-sm text-gray-500 mt-1">Ranked by total sales</p>
          </div>

          <div className="overflow-x-auto">

            <GTable
              columns={topPerformingCompaniesColumns}
              data={data?.topPerformingCompanies
                .filter(company => company.type === 'INSURER')
                .slice(0, 5) || []}
              showSearch={false}
              tableType="approval"
              columnClasses=""
              loading={isLoadingCompanyStatistics}
              classes="h-fit"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyStatisticsPanel;


export interface ICompanyStatistics {
  usersByCompanyType: {
    AGENCY: number;
    BROKER: number;
    INSURER: number;
    REPAIR_CENTER: number;
    INTERNAL: number;
    RIDE_SHARING: number;
    FINANCIER: number;
  },
  userMetrics: {
    totalUsers: number;
    averageUsersPerCompany: number;
    inactiveUsers: number;
    activeUsers: number;
  },
  activeCompaniesByType: {
    OTHER: number;
    AGENCY: number;
    BROKER: number;
    INSURER: number;
    REPAIR_CENTER: number;
    INTERNAL: number;
    FUEL_PROVIDER: number;
    RIDE_SHARING: number;
    FINANCIER: number;
  },
  topPerformingCompanies: {
    userCount: number;
    policyCount: number;
    name: string;
    totalSales: number;
    id: number;
    type: string;
    category: string;
    createdOn: string;
  }[],
  activationRate: number,
  salesCountByType: Record<string, number>,
  inactiveCompanies: number,
  avgPoliciesPerCompany: number,
  companiesByType: {
    OTHER: number;
    AGENCY: number;
    BROKER: number;
    INSURER: number;
    REPAIR_CENTER: number;
    INTERNAL: number;
    FUEL_PROVIDER: number;
    RIDE_SHARING: number;
    FINANCIER: number;
  },
  policiesByCompanyType: {
    INTERNAL: number;
  },
  activeCompanies: number,
  companiesWithoutUsers: number,
  salesAmountByType: Record<string, number>,
  recentRegistrations: number,
  avgSalesPerCompany: number,
  totalCompanies: number,
  companiesWithoutSales: number,
  activeCompaniesByCategory: {
    PARTNER: number;
    OTHER: number;
    SERVICE_PROVIDER: number;
    INTERNAL: number;
    CLIENT: number;
  },
  companiesByCategory: {
    PARTNER: number;
    OTHER: number;
    SERVICE_PROVIDER: number;
    INTERNAL: number;
    CLIENT: number;
  }
}
